import { ApiRequestError, ApiTimeoutError } from '@/lib/api-error';
import type { OfflineStore } from './store';
import {
  PDA_SYNC_BATCH_MAX,
  type PdaSyncPull,
  type PdaSyncPushInput,
  type PdaSyncPushResult,
  type PdaSyncResult,
} from '@fuel-system/contracts';
import type { OutboxOperation, RecentOperation } from './types';

/** The two sync endpoints (fuel-pda.md 13.7); injected so the engine is testable. */
export interface SyncTransport {
  push(input: PdaSyncPushInput): Promise<PdaSyncPushResult>;
  /** `deviceId` binds the grant to this install; the same id goes with every push. */
  pull(since: string | null, deviceId: string): Promise<PdaSyncPull>;
}

/**
 * A failure that means "the server cannot be reached", not "the server said
 * no": the request never got an answer, or a gateway in front of the API
 * answered that the API is down.
 */
export function isNetworkFailure(error: unknown): boolean {
  if (error instanceof ApiTimeoutError) return true;
  if (error instanceof TypeError) return true;
  if (error instanceof DOMException && error.name === 'AbortError') return true;
  return error instanceof ApiRequestError && [502, 503, 504].includes(error.status);
}

export interface PushSummary {
  accepted: number;
  waiting: number;
  failed: number;
}

async function updateRecent(
  store: OfflineStore,
  id: string,
  change: (entry: RecentOperation) => RecentOperation,
): Promise<void> {
  const entry = await store.recentById(id);
  if (entry) await store.putRecent(change(entry));
}

async function applyResult(
  store: OfflineStore,
  operation: OutboxOperation,
  result: PdaSyncResult,
): Promise<'accepted' | 'waiting' | 'failed'> {
  const reference = (operation.data as { reference?: unknown } | null)?.reference;
  const saleReference = typeof reference === 'string' ? reference : null;

  if (result.status === 'WAITING') {
    await store.putOutbox({
      ...operation,
      attempts: operation.attempts + 1,
      ...(result.code ? { lastCode: result.code } : {}),
    });
    return 'waiting';
  }

  await store.removeOutbox(operation.id);
  if (operation.kind === 'DELIVERY' && saleReference) {
    await store.removeLocalDelivery(saleReference);
    // A REFUSED delivery was never applied: the sale may still be delivered online.
    if (result.status !== 'REFUSED') await store.removePendingSale(saleReference);
  }

  if (result.status === 'ACCEPTED') {
    await updateRecent(store, operation.id, (entry) => ({
      ...entry,
      reference: result.reference ? result.reference.toUpperCase() : entry.reference,
      syncState: 'SYNCED',
      failureCode: null,
      failureParams: null,
    }));
    return 'accepted';
  }

  await updateRecent(store, operation.id, (entry) => ({
    ...entry,
    syncState: 'FAILED',
    failureCode: result.code ?? result.status,
    failureParams: result.details ?? null,
  }));
  return 'failed';
}

/**
 * Sends the outbox in device order, in batches of up to 50 (section 13.7).
 * ACCEPTED, CONFLICT and REFUSED leave the outbox; WAITING stays and stops
 * this run, so a later operation never overtakes one that waits. A network
 * failure is thrown to the caller with the outbox unchanged.
 */
export async function pushOutbox(
  store: OfflineStore,
  transport: SyncTransport,
  deviceId: string,
): Promise<PushSummary> {
  const summary: PushSummary = { accepted: 0, waiting: 0, failed: 0 };
  const operations = await store.outbox();
  for (let start = 0; start < operations.length; start += PDA_SYNC_BATCH_MAX) {
    const batch = operations.slice(start, start + PDA_SYNC_BATCH_MAX);
    const answer = await transport.push({
      deviceId,
      operations: batch.map(({ id, seq, kind, grant, enteredAt, data }) => ({
        id,
        seq,
        kind,
        grant,
        enteredAt,
        data,
      })),
    });
    const byId = new Map(answer.results.map((result) => [result.id, result]));
    let stop = false;
    for (const operation of batch) {
      const result = byId.get(operation.id);
      if (!result) {
        // No answer for this one: it stays and is sent again next time.
        await store.putOutbox({ ...operation, attempts: operation.attempts + 1 });
        summary.waiting += 1;
        stop = true;
        continue;
      }
      const outcome = await applyResult(store, operation, result);
      summary[outcome] += 1;
      if (outcome === 'waiting') stop = true;
    }
    if (stop) break;
  }
  return summary;
}

/** The business date before `date` (YYYY-MM-DD). */
export function previousDate(date: string): string {
  const day = new Date(`${date}T00:00:00Z`);
  day.setUTCDate(day.getUTCDate() - 1);
  return day.toISOString().slice(0, 10);
}

/**
 * Downloads the context, keys, grant and the changed customers and pending
 * sales (section 13.7). The cursor is reused only for the same branch; the
 * first pull, or a pull for another branch, replaces every list.
 */
export async function pullInto(
  store: OfflineStore,
  transport: SyncTransport,
  branchId: string,
  deviceId: string,
): Promise<PdaSyncPull> {
  const cachedContext = await store.getValue('context');
  const cursor = cachedContext?.branch.id === branchId ? await store.getValue('cursor') : undefined;
  const pulled = await transport.pull(cursor ?? null, deviceId);
  const replace = !cursor;

  await store.applyCustomers(pulled.customers.items, pulled.customers.removedIds, replace);
  await store.applyPendingSales(
    pulled.pendingSales.items,
    pulled.pendingSales.removedReferences,
    replace,
    pulled.serverTime,
  );
  await store.setValue('context', pulled.context);
  await store.setValue('nationalities', pulled.nationalities);
  await store.setValue('qrKeys', pulled.qrKeys);
  await store.setValue('grant', pulled.offlineGrant);
  await store.setValue('cursor', pulled.serverTime);
  const lastSeen = await store.getValue('serverTime');
  if (!lastSeen || Date.parse(pulled.serverTime) > Date.parse(lastSeen)) {
    await store.setValue('serverTime', pulled.serverTime);
  }
  const today = pulled.context.businessDate;
  await store.pruneRecent([today, previousDate(today)]);
  return pulled;
}
